import { useState, type FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/auth'
import { loadProfile } from '@/hooks/useAuth'
import { Capacitor } from '@capacitor/core'
import { AuthLayout } from '@/components/gg/AuthLayout'

// Accounts are created on the website only; the native app signs in.
const isNative = Capacitor.isNativePlatform()

export function Login() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [resetSent, setResetSent] = useState(false)
  const [resetting, setResetting] = useState(false)

  async function handleReset() {
    if (!email) {
      setError('Enter your email address first, then choose "Forgot password".')
      return
    }
    setError(null)
    setResetting(true)
    const { error } = await supabase.auth.resetPasswordForEmail(email)
    setResetting(false)
    if (error) setError(error.message)
    else setResetSent(true)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error || !data.session) {
      setError(error?.message ?? 'Sign in failed. Try again.')
      setLoading(false)
      return
    }

    // onAuthStateChange fires asynchronously; populate the store from the result
    // first so AuthGuard can't observe a signed-out state and bounce us back.
    const { setSession, setLoading: setAuthLoading } = useAuthStore.getState()
    setSession(data.session)
    await loadProfile(data.session.user.id)
    setAuthLoading(false)
    navigate('/app', { replace: true })
  }

  return (
    <AuthLayout
      eyebrow="Welcome back"
      title="Sign in"
      foot={<>By signing in you agree to our <Link to="/terms">terms</Link> and <Link to="/privacy">privacy policy</Link>.</>}
    >
      <form onSubmit={(e) => { void handleSubmit(e) }}>
        <label className="gg-field">
          <span>Email</span>
          <input className="gg-input" id="email" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} required autoComplete="email" inputMode="email" />
        </label>
        <label className="gg-field">
          <span>Password</span>
          <input className="gg-input" id="password" type="password" value={password}
            onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
        </label>
        {error && <p className="gg-error" role="alert">{error}</p>}
        {resetSent && (
          <p className="gg-note">
            If an account exists for <strong>{email}</strong>, a password reset link is on its way.
          </p>
        )}
        <button type="submit" disabled={loading} className="gg-btn gg-btn--amber gg-btn--block auth2__submit">
          {loading ? 'Signing in' : 'Sign in'}
        </button>
      </form>
      <div className="auth2__links">
        <button type="button" className="auth2__link" onClick={() => { void handleReset() }} disabled={resetting}>
          {resetting ? 'Sending the link' : 'Forgot password?'}
        </button>
        {!isNative && <Link to="/signup" className="auth2__link">Need an account? <b>Sign up</b></Link>}
      </div>
    </AuthLayout>
  )
}
